import React, { useState, useEffect } from "react";
import { defaultMaxStartDateRangeDate, defaultStartDateRangeDate } from "src/helpers";
import { TransferOrdersFilters, DateTimeRange, TOCompletionStatus } from "src/types";
import { CreationDateSortOptions, TransferOrderColumnHeader } from "src/enums";
import { DropdownField } from "src/components/ui/inputs";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { Badge } from "src/shadcn/ui/badge";
import { Trash2 } from "lucide-react";

const TO_COMPLETION_STATUS_OPTIONS: TOCompletionStatus[] = ["In Progress", "Complete"];

interface FilterTransferOrdersProps {
  onFilterChange: (filters: TransferOrdersFilters) => void;
  currentFilter?: TransferOrdersFilters;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  sortOption: CreationDateSortOptions;
  onSortChange: (option: CreationDateSortOptions) => void;
  columnsToHide: TransferOrderColumnHeader[];
  onColumnsToHideChange: (columns: TransferOrderColumnHeader[]) => void;
  allColumns: TransferOrderColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
}

export const FilterTransferOrders: React.FC<FilterTransferOrdersProps> = ({
  onFilterChange,
  currentFilter,
  dateTimeRange,
  onDateTimeRangeChange,
  sortOption,
  onSortChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen
}) => {
  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localSortOption, setLocalSortOption] = useState<CreationDateSortOptions>(sortOption);

  const [filters, setFilters] = useState<Partial<TransferOrdersFilters>>({
    completionStatusFilter: currentFilter?.completionStatusFilter
  });

  useEffect(() => {
    if (isOpen) {
      setLocalDateTimeRange(dateTimeRange);
      setLocalSortOption(sortOption);
      setFilters({ completionStatusFilter: currentFilter?.completionStatusFilter });
    }
  }, [isOpen, dateTimeRange, sortOption, currentFilter]);

  const handleFilterChange = <K extends keyof TransferOrdersFilters>(key: K, value: TransferOrdersFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const updatedFilters: TransferOrdersFilters = {
      ...filters,
      filterPopoverVisible: false
    } as TransferOrdersFilters;

    onDateTimeRangeChange(localDateTimeRange);
    onSortChange(localSortOption);
    onFilterChange(updatedFilters);

    onClose?.();
  };

  const handleClearAllFilters = () => {
    const clearedFilters: TransferOrdersFilters = {
      completionStatusFilter: undefined,
      filterPopoverVisible: false
    };
    setFilters(clearedFilters);

    const defaultRange = { start: defaultStartDateRangeDate(), end: undefined };
    setLocalDateTimeRange(defaultRange);
    onDateTimeRangeChange(defaultRange);

    setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC);
    onSortChange(CreationDateSortOptions.CREATION_DATE_DESC);

    onFilterChange(clearedFilters);
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.completionStatusFilter) count++;
    if (localDateTimeRange.start) {
      const defaultStart = defaultStartDateRangeDate();
      const isDefault =
        localDateTimeRange.start.getFullYear() === defaultStart.getFullYear() &&
        localDateTimeRange.start.getMonth() === defaultStart.getMonth() &&
        localDateTimeRange.start.getDate() === defaultStart.getDate() &&
        localDateTimeRange.start.getHours() === defaultStart.getHours() &&
        localDateTimeRange.start.getMinutes() === defaultStart.getMinutes();
      if (!isDefault) count++;
    }
    if (localDateTimeRange.end) count++;
    if (localSortOption !== CreationDateSortOptions.CREATION_DATE_DESC) count++;
    return count;
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    if (!value) {
      setLocalDateTimeRange({ ...localDateTimeRange, [field]: undefined });
      return;
    }

    const newDate = new Date(value);
    const maxStartDate = defaultMaxStartDateRangeDate();
    const maxEndDate = new Date();

    if (field === "start") {
      const maxAllowedStart = localDateTimeRange.end ? new Date(localDateTimeRange.end) : maxEndDate;
      const clampedStart =
        newDate < maxStartDate ? maxStartDate : newDate > maxAllowedStart ? maxAllowedStart : newDate;

      setLocalDateTimeRange({
        start: clampedStart,
        end: localDateTimeRange.end && clampedStart > localDateTimeRange.end ? clampedStart : localDateTimeRange.end
      });
    } else {
      const minAllowedEnd = localDateTimeRange.start ? new Date(localDateTimeRange.start) : maxStartDate;
      const clampedEnd = newDate < minAllowedEnd ? minAllowedEnd : newDate > maxEndDate ? maxEndDate : newDate;

      setLocalDateTimeRange({
        ...localDateTimeRange,
        end: clampedEnd
      });
    }
  };

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters() > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters()}
              </Badge>
            )}
          </div>
          {countActiveFilters() > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          <DropdownField
            label="Completion Status"
            value={filters.completionStatusFilter}
            onChange={(value) => handleFilterChange("completionStatusFilter", value as TOCompletionStatus | undefined)}
            options={TO_COMPLETION_STATUS_OPTIONS}
          />

          {/* Creation Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - Start
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.start)}
              onChange={(e) => handleDateChange("start", e.target.value)}
              min={formatDateForInput(defaultMaxStartDateRangeDate())}
              max={formatDateForInput(localDateTimeRange.end || new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - End
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.end)}
              onChange={(e) => handleDateChange("end", e.target.value)}
              min={formatDateForInput(localDateTimeRange.start || defaultMaxStartDateRangeDate())}
              max={formatDateForInput(new Date())}
              className="text-sm h-9"
            />
          </div>

          {/* Sort */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>
        </div>

        {/* Apply Button */}
        <div className="sticky -bottom-4 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
          <Button
            onClick={handleApplyFilters}
            className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
          >
            Apply Changes
          </Button>
        </div>
      </div>

      {/* Column Visibility */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible
                    ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100"
                    : "bg-transparent text-secondary-400 hover:bg-secondary-50"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge
                  variant={isVisible ? "default" : "outline"}
                  className={`text-xs px-1.5 py-0 h-4 flex-shrink-0 ml-2 ${isVisible ? "bg-green-500 hover:bg-green-600" : ""}`}
                >
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
